/**
 * Hook para autenticação Firebase
 * Gerencia login/logout com Google
 */

import { useState, useCallback } from "react";
import { signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";

/**
 * Hook para gerenciar autenticação Firebase
 * @returns {Object} Métodos e estados de autenticação Firebase
 */
export function useFirebaseAuth() {
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  /**
   * Login com Google
   */
  const loginWithGoogle = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  /**
   * Logout do Firebase
   */
  const logout = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      // Limpar dados do localStorage
      try {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("churchUser");
      } catch (storageError) {
        console.warn("[useFirebaseAuth] Erro ao limpar localStorage:", storageError);
      }

      // Fazer logout do Firebase Auth
      await signOut(auth);
    } catch (error) {
      setAuthError(error.message);
      // Garantir limpeza do localStorage mesmo com erro
      try {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("churchUser");
      } catch (storageError) {
        console.warn("[useFirebaseAuth] Erro ao limpar localStorage no fallback:", storageError);
      }
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  /**
   * Limpar erro
   */
  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  return {
    // Estado
    loading: authLoading,
    error: authError,

    // Métodos
    loginWithGoogle,
    logout,
    clearError,
  };
}

export default useFirebaseAuth;
